// PaymentService: provides password verification and (placeholder) payment processing
// This bridges older docs expecting paymentService with the new iciciPaymentService

import authService from './authService';

class TestingPaymentService {
    constructor() {
        this.apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';
    }

    // Initiate ICICI Payment - matches your PaymentController.initiateICICIPayment
    async initiateICICIPayment(paymentData) {
        try {
            const token = authService.getToken();
            
            console.log('Initiating ICICI payment with data:', paymentData);
            
            const response = await fetch(`${this.apiBaseUrl}/payment/initiate-icici`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : undefined
                },
                body: JSON.stringify(paymentData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log('ICICI payment initiation response:', result);
            
            return result;
        } catch (error) {
            console.error('Error initiating ICICI payment:', error);
            throw error;
        }
    }

    // Handle payment response from ICICI gateway
    async handlePaymentResponse(responseData) {
        try {
            const token = authService.getToken();
            
            const response = await fetch(`${this.apiBaseUrl}/payment/response`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : undefined
                },
                body: JSON.stringify(responseData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error handling payment response:', error);
            throw error;
        }
    }

    // Get payment status by order ID
    async getPaymentStatus(orderId) {
        try {
            const token = authService.getToken();
            
            const response = await fetch(`${this.apiBaseUrl}/payment/status/${orderId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : undefined
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error getting payment status:', error);
            throw error;
        }
    }

    // Verify login password (proxy to auth endpoint you already have or mock)
    async verifyPaymentPassword(orgId, password) {
        try {
            const response = await fetch(`${this.apiBaseUrl}/auth/verify-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ orgId, password })
            });
            if (!response.ok) {
                return { success: false, isValid: false, message: 'Verification failed' };
            }
            const data = await response.json();
            return {
                success: true,
                isValid: data.isValid || data.success || false,
                message: data.message || 'OK'
            };
        } catch (err) {
            console.error('verifyPaymentPassword error', err);
            return { success: false, isValid: false, message: err.message };
        }
    }

    // Legacy method for backward compatibility
    async processPayment(paymentData) {
        return this.initiateICICIPayment(paymentData);
    }

    // Check if payment is completed based on localStorage or URL params
    async checkPaymentCompletion() {
        try {
            // Check localStorage for recent successful payments
            const paymentResponses = JSON.parse(localStorage.getItem('paymentResponses') || '[]');
            const recentPayment = paymentResponses
                .filter(p => p.finalStatus === 'success')
                .sort((a, b) => new Date(b.processedAt) - new Date(a.processedAt))[0];

            if (recentPayment) {
                // Check if payment is within last 30 minutes
                const paymentTime = new Date(recentPayment.processedAt);
                const now = new Date();
                const diffMinutes = (now - paymentTime) / (1000 * 60);
                
                if (diffMinutes <= 30) {
                    return {
                        success: true,
                        isCompleted: true,
                        paymentDetails: recentPayment,
                        message: 'Payment completed successfully'
                    };
                }
            }

            return {
                success: true,
                isCompleted: false,
                message: 'No recent successful payment found'
            };
        } catch (error) {
            console.error('Error checking payment completion:', error);
            return {
                success: false,
                isCompleted: false,
                message: 'Error checking payment status'
            };
        }
    }

    // Check payment status from PaymentGateWayTracking table
    async checkPaymentStatus(orgId, appId) {
        try {
            const token = authService.getToken();
            
            const response = await fetch(`${this.apiBaseUrl}/payment/check-status`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : undefined
                },
                body: JSON.stringify({ orgId, appId })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error checking payment status:', error);
            throw error;
        }
    }

    // Clear payment completion status
    clearPaymentCompletion() {
        try {
            localStorage.removeItem('paymentResponses');
            localStorage.removeItem('currentOrderId');
        } catch (error) {
            console.error('Error clearing payment completion:', error);
        }
    }

    // Offline payment submission placeholder
    async submitOfflinePayment(data) {
        return { success: false, message: 'Offline payment endpoint not implemented' };
    }
}

const TestingPaymentServiceInstance = new TestingPaymentService();
export default TestingPaymentServiceInstance;